<template>
    <DemoToolsLauncher v-if="tools || proactiveOfferUrl" ref="launcher" v-model:open="open" title="Chat demo tools">
        <div class="flex flex-col gap-5">
            <section v-if="tools" class="flex flex-col gap-3" aria-labelledby="chat-demo-generate-heading">
                <h2 id="chat-demo-generate-heading" class="body-small-bold text-ink">Generated messages</h2>
                <FormElement title="Conversation script" for="chat-demo-script">
                    <BaseTextarea
                        id="chat-demo-script"
                        v-model:input="script"
                        rows="3"
                        class="rounded-lg border border-control-border bg-control-bg p-3 focus-field"
                        placeholder="e.g. A new manager preparing for a tough 1:1 with a direct report" />
                </FormElement>
                <BaseButton theme="primary" :disabled="generationDisabled" :aria-busy="isGenerating" @click="generate({})">
                    {{ isGenerating ? "Generating…" : "Generate into composer" }}
                </BaseButton>
                <p class="body-small text-ink-muted">Press Ctrl+G (or Cmd+G) to generate into the composer.</p>
            </section>

            <section v-if="draft" class="flex flex-col gap-3" aria-labelledby="chat-demo-draft-heading">
                <h2 id="chat-demo-draft-heading" class="body-small-bold text-ink">Generated draft</h2>
                <p class="body-small text-ink whitespace-pre-wrap">{{ draft }}</p>
                <BaseButton theme="neutral" size="sm" :disabled="!composerAvailable" @click="insertDraft">
                    {{ composerHasDraft ? "Replace composer with generated draft" : "Use generated draft" }}
                </BaseButton>
            </section>

            <section v-if="tools" class="flex flex-col gap-3" aria-labelledby="chat-demo-helpers-heading">
                <h2 id="chat-demo-helpers-heading" class="body-small-bold text-ink">Exercise Nadia</h2>
                <div class="flex flex-col gap-2">
                    <label for="chat-demo-guardrail" class="flex flex-col gap-2 body-small text-ink-muted">
                        Guardrail
                        <select id="chat-demo-guardrail" v-model="guardrail" class="demo-select body-small">
                            <option v-for="key in tools.guardrails" :key="key" :value="key">{{ guardrailLabel(key) }}</option>
                        </select>
                    </label>
                    <BaseButton theme="neutral" size="sm" :disabled="generationDisabled || !guardrail" @click="generate({ helper: 'guardrail', guardrail })">
                        Trigger a guardrail
                    </BaseButton>
                </div>
                <div class="flex flex-col gap-2">
                    <label for="chat-demo-person" class="flex flex-col gap-2 body-small text-ink-muted">
                        Person
                        <select id="chat-demo-person" v-model="personName" class="demo-select body-small">
                            <option value="">Someone new</option>
                            <option v-for="name in personNames" :key="name" :value="name">{{ name }}</option>
                        </select>
                    </label>
                    <BaseButton
                        theme="neutral"
                        size="sm"
                        :disabled="generationDisabled"
                        @click="generate({ helper: 'person', ...(personName ? { person_name: personName } : {}) })">
                        Reference a person
                    </BaseButton>
                </div>
                <BaseButton
                    v-for="helper in simpleHelpers"
                    :key="helper.value"
                    theme="neutral"
                    size="sm"
                    :disabled="generationDisabled"
                    @click="generate({ helper: helper.value })">
                    {{ helper.label }}
                </BaseButton>
            </section>

            <div aria-live="polite">
                <p v-if="errorMessage" class="body-small text-status-error">{{ errorMessage }}</p>
            </div>

            <ProactiveOfferDemoTools v-if="proactiveOfferUrl" :url="proactiveOfferUrl" :disabled="disabled" @generated="emit('offer-generated')" />
        </div>
    </DemoToolsLauncher>
</template>

<script lang="ts">
export interface ChatDemoToolsConfig {
    generate_message_url: string;
    guardrails: string[];
}

type ChatDemoHelper = "guardrail" | "person" | "role_play" | "email_draft" | "learning_content" | "upcoming_meetings";

interface HelperRequest {
    helper?: ChatDemoHelper;
    guardrail?: string;
    person_name?: string;
}
</script>

<script setup lang="ts">
import { useEventListener, useStorage } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { GlobalProperties, UserDetails } from "~vue/appTypes";
import ProactiveOfferDemoTools from "~vue/ChatDetail/ProactiveOfferDemoTools.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import FormElement from "~vue/components/form/FormElement.vue";
import DemoToolsLauncher from "~vue/DemoTools/DemoToolsLauncher.vue";
import { CHAT_EVENT } from "~vue/events";
import { computed, inject, type Ref, ref, useTemplateRef, watch } from "vue";

const {
    tools,
    proactiveOfferUrl = null,
    promptId,
    connections = [],
    disabled = false,
    composerAvailable = true,
    composerHasDraft = false,
} = defineProps<{
    tools: ChatDemoToolsConfig | null;
    proactiveOfferUrl?: string | null;
    promptId: number;
    connections?: { name: string }[];
    disabled?: boolean;
    composerAvailable?: boolean;
    composerHasDraft?: boolean;
}>();
const emit = defineEmits<{ "offer-generated": [] }>();

const { emitter } = inject<GlobalProperties>("globalProperties")!;
const userDetails = inject<Ref<UserDetails>>("user_details");

const simpleHelpers: { value: ChatDemoHelper; label: string }[] = [
    { value: "role_play", label: "Start a role play" },
    { value: "email_draft", label: "Draft an email" },
    { value: "learning_content", label: "Recommend learning content" },
    { value: "upcoming_meetings", label: "Ask about upcoming meetings" },
];

const open = ref(false);
const launcher = useTemplateRef("launcher");
const draft = ref<string | null>(null);
const autoInsertDraft = ref(false);
// Shared with DevTools so both generators use the same script.
const script = useStorage(`dev_llm_theme_${promptId}`, "");
const guardrail = ref(tools?.guardrails[0] ?? "");
const personName = ref("");
const isGenerating = ref(false);
const errorMessage = ref("");

const generationDisabled = computed(() => !composerAvailable || isGenerating.value);
const personNames = computed(() => [...new Set(connections.map(({ name }) => name).filter(Boolean))]);

watch(
    [draft, () => composerAvailable, () => composerHasDraft],
    ([message, available, hasDraft]) => {
        if (hasDraft) {
            autoInsertDraft.value = false;
        }
        if (message !== null && available && autoInsertDraft.value) {
            insertDraft();
        }
    },
    { flush: "post" },
);

function insertDraft(): void {
    if (draft.value !== null && composerAvailable) {
        emitter.emit(CHAT_EVENT.SET_MESSAGE_TEXT, draft.value);
        draft.value = null;
        autoInsertDraft.value = false;
        launcher.value?.finish({ theme: "info", text: "Your generated message is ready to edit in the composer." });
    }
}

function reportError(text: string): void {
    errorMessage.value = text;
    if (!open.value) {
        launcher.value?.finish({ theme: "error", text: errorMessage.value });
    }
}

function guardrailLabel(key: string): string {
    const label = key.replaceAll("_", " ");
    return label.charAt(0).toUpperCase() + label.slice(1);
}

async function generate(request: HelperRequest): Promise<void> {
    if (!tools || isGenerating.value) {
        return;
    }
    if (!composerAvailable) {
        reportError("The composer is unavailable during role-play setup.");
        return;
    }
    isGenerating.value = true;
    errorMessage.value = "";
    try {
        const { data, error } = await useFetch(tools.generate_message_url)
            .post({ theme: script.value, ...request })
            .json<{ generated_message: string }>();
        const message = data.value?.generated_message;
        if (error.value || !message) {
            reportError("Couldn't generate a message. Please try again.");
            return;
        }
        autoInsertDraft.value = !composerHasDraft;
        draft.value = message;
        if (composerHasDraft) {
            open.value = false;
        } else if (!composerAvailable) {
            launcher.value?.finish({
                theme: "info",
                text: "Your generated message will appear when the composer returns.",
            });
        }
    } catch {
        reportError("Couldn't generate a message. Please try again.");
    } finally {
        isGenerating.value = false;
    }
}

useEventListener(document, "keydown", (event: KeyboardEvent) => {
    if (!tools || userDetails?.value.is_staff !== true || event.defaultPrevented || (event.target instanceof Element && event.target.closest("dialog[open]"))) {
        return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key === "g" && !event.shiftKey) {
        event.preventDefault();
        generate({});
    }
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .demo-select {
        @apply w-full rounded-lgr border border-valence-grey-200 bg-white px-3 py-2 text-valence-grey-800;
    }
}
</style>
